import { isAxiosError } from 'axios';

import { v1Api } from '@/api/backendApi';
import { setlist } from '@/utils/typesense';
import { getApiV1TrackCollectionsPageSlug } from '@/open-api/collections/collections';
import { getApiV1SfxCollectionsPageSlug } from '@/open-api/sfx-collections/sfx-collections';
import { getApiV1TrackTagsPageSlug } from '@/open-api/track-tags/track-tags';

import type {
  GetSfxCollectionPageResponse,
  GetTrackCollectionPageResponse,
} from '@/open-api/model';
import type { AmbiguousLinks } from '@/types/ambiguousLinks';
import type { Collection } from '@/types/collection';
import type { Redirect } from '@/types/page';
import type { TagCollection } from '@/types/tags';
import type { Taxonomy, TaxonomyBreadcrumbParent } from '@/types/Taxonomy';

export interface CategoryHeader {
  ambiguous_links?: AmbiguousLinks;
  breadcrumb_name?: string;
  breadcrumb_parents?: TaxonomyBreadcrumbParent[];
  collections: TagCollection[];
  description: string | null;
  discord_url?: string;
  excluded_keywords?: string;
  // Music only: the page's rule compiled to a Typesense `filter_by`.
  filter?: string | null;
  headercount: number;
  id: number;
  inspired_by?: boolean;
  instagram_url?: string;
  meta_description: string;
  meta_title: string;
  name: string;
  name_sentence_case: string;
  order: number;
  pageType?: string;
  partner_name?: string;
  primary_heading?: string;
  primary_heading_visitor?: string;
  schema_description?: string;
  search_term?: string;
  short_description?: string | null;
  show_uppbeat_logo?: boolean;
  slug: string;
  slugId?: number;
  target_ksp?: string;
  target_phrase?: string;
  target_word?: string;
  taxonomy: Taxonomy[];
  tiktok_url?: string;
  tile_image: string;
  twitter_url?: string;
  type?: 'style' | 'tag';
  url: string;
  vector_distance_threshold?: number;
  youtube_url?: string;
}

// The tag page is addressed by slug (the last path segment) and no longer
// answers for old paths, so a miss falls back to the page-redirects lookup.
// The caller compares the returned `url` with the path being rendered.
const getCategory = async (
  slug: string[],
  source: string
): Promise<CategoryHeader | Redirect | null> => {
  try {
    // Same JSON as the PageApi endpoint it replaces, plus `filter`.
    return (await getApiV1TrackTagsPageSlug(
      encodeURIComponent(slug.at(-1) ?? '')
    )) as unknown as CategoryHeader;
  } catch (error) {
    if (!(isAxiosError(error) && error.response?.status === 404)) {
      throw error;
    }

    const redirect = await setlist.getPageRedirect(source).catch(() => null);
    // The lookup is a token match, so only trust an exact source.
    if (redirect?.source === source) {
      return {
        destination: redirect.destination,
        permanent: redirect.permanent,
      };
    }

    // Unknown tag: null so the page 404s without reporting an error.
    return null;
  }
};

type CollectionPageResponse = NonNullable<
  GetTrackCollectionPageResponse | GetSfxCollectionPageResponse
>;

// Copy a field only when it has a value: the PHP API omitted nulls, and
// getServerSideProps can't serialize undefined.
const optional = <K extends keyof Collection>(
  key: K,
  value: Collection[K] | null | undefined
) => (value === null || value === undefined ? {} : { [key]: value });

/**
 * Map the .NET collection page response onto the snake_case Collection the
 * pages already consume. `thumbnail_image` is only set when there is an image,
 * as before (`isCollection` keys off its presence).
 */
export const mapCollectionPage = (
  collection: CollectionPageResponse
): Collection => {
  const track: Partial<NonNullable<GetTrackCollectionPageResponse>> =
    collection;

  return {
    id: collection.id,
    name: collection.name,
    slug: collection.slug ?? '',
    ...optional('description', collection.description),
    ...optional('short_description', collection.shortDescription),
    ...optional('meta_title', collection.metaTitle),
    ...optional('meta_description', collection.metaDescription),
    ...optional(
      'thumbnail_image',
      collection.thumbnailImageUrl
        ? { image_aws_url: collection.thumbnailImageUrl }
        : null
    ),
    ...optional('inspired_by', track.inspiredBy),
    ...optional('celebration_date', track.celebrationDate),
    ...optional('overlay_colour', track.overlayColour),
    ...optional('partner_name', track.partnerName),
    ...optional('instagram_url', track.instagramUrl),
    ...optional('youtube_url', track.youTubeUrl),
    ...optional('twitter_url', track.twitterUrl),
    ...optional('tiktok_url', track.tikTokUrl),
  };
};

/**
 * Fetch a collection page, resolving to null when it doesn't exist or is
 * offline (404) so the page 404s without reporting an error.
 */
const fetchCollectionPage = async (
  request: () => Promise<CollectionPageResponse | null>
): Promise<Collection | null> => {
  try {
    const collection = await request();

    return collection ? mapCollectionPage(collection) : null;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }

    throw error;
  }
};

// Collections are just a single slug.
const getCollection = (collectionSlug: string[]) =>
  fetchCollectionPage(() =>
    getApiV1TrackCollectionsPageSlug(encodeURIComponent(collectionSlug[0]))
  );

// SFX
const getSFXCollection = (slug: string[]) =>
  fetchCollectionPage(() =>
    getApiV1SfxCollectionsPageSlug(encodeURIComponent(slug[0]))
  );

const getSfxCategory = async (slug: string[], source: string) =>
  v1Api
    .get<CategoryHeader | Redirect>(
      `/page/sfx/category?source=${source}&version=2`
    )
    .then((res) => res.data);

export const pageDataApiMap = {
  music: {
    category: getCategory,
    collection: getCollection,
  },
  sfx: {
    category: getSfxCategory,
    collection: getSFXCollection,
  },
};
